import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MailCheck, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const fieldCls = "rounded-xl border-2 border-monster-navy bg-white px-3 py-2 text-sm font-semibold shadow-sticker-sm";

export const AdminSubscribers = () => {
    const [subs, setSubs] = useState(null);
    const [code, setCode] = useState("MONSTER20");
    const [message, setMessage] = useState("");
    const [confirming, setConfirming] = useState(false);
    const [sending, setSending] = useState(false);
    const [result, setResult] = useState(null);

    const load = () => {
        api.get("/subscribers")
            .then((res) => setSubs(res.data))
            .catch((err) => {
                toast.error(formatApiError(err));
                setSubs([]);
            });
    };

    useEffect(load, []);

    const remove = async (id) => {
        try {
            await api.delete(`/subscribers/${id}`);
            toast.success("Subscriber removed");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const blast = async () => {
        if (!confirming) {
            setConfirming(true);
            return;
        }
        setConfirming(false);
        setSending(true);
        setResult(null);
        try {
            const res = await api.post("/subscribers/blast", { code, message: message || null });
            setResult(res.data);
            toast.success(`Launch email sent to ${res.data.sent} subscriber${res.data.sent === 1 ? "" : "s"}`);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSending(false);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ type: "spring", stiffness: 100, damping: 15 }}
            className="space-y-8"
        >
            <div className="rounded-3xl border-2 border-monster-navy bg-monster-navy p-6 shadow-sticker sm:p-8">
                <h2 className="font-display text-xl font-semibold text-white">Launch Day Blast</h2>
                <p className="mt-1 text-sm font-semibold text-white/60">
                    One click emails everyone on your launch list with their 20% off code. Save it for opening day.
                </p>
                <div className="mt-6 grid gap-4 sm:grid-cols-2">
                    <div>
                        <Label htmlFor="blast-code" className="mb-1.5 block font-display text-xs font-semibold text-white/60">Discount code</Label>
                        <Input
                            id="blast-code"
                            data-testid="blast-code-input"
                            value={code}
                            onChange={(e) => setCode(e.target.value)}
                            className={`${fieldCls} uppercase tracking-widest`}
                        />
                    </div>
                    <div className="sm:col-span-2">
                        <Label htmlFor="blast-message" className="mb-1.5 block font-display text-xs font-semibold text-white/60">Personal note (optional)</Label>
                        <Textarea
                            id="blast-message"
                            data-testid="blast-message-input"
                            rows={2}
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder="e.g. First 10 bookings get a free concession rental!"
                            className={fieldCls}
                        />
                    </div>
                </div>
                <div className="mt-5 flex flex-wrap items-center gap-4">
                    <button
                        data-testid="blast-send-btn"
                        onClick={blast}
                        disabled={sending || !code.trim() || !subs || subs.length === 0}
                        className={`inline-flex items-center gap-2 rounded-full border-2 border-monster-navy px-6 py-3 font-display text-sm font-semibold shadow-sticker-sm transition-all hover:-translate-y-0.5 active:translate-y-1 active:shadow-none disabled:opacity-50 ${
                            confirming ? "bg-monster-red text-white" : "bg-monster-green text-monster-navy"
                        }`}
                    >
                        <Send className="h-4 w-4" />
                        {sending
                            ? "Sending..."
                            : confirming
                              ? `Really email all ${subs?.length ?? 0}? Click again`
                              : "Email Everyone"}
                    </button>
                    {result && (
                        <p data-testid="blast-result" className="text-sm font-bold text-monster-green">
                            Sent {result.sent} of {result.total}{result.failed > 0 ? ` — ${result.failed} failed` : ""}
                        </p>
                    )}
                </div>
            </div>

            <div className="overflow-hidden rounded-3xl border-2 border-monster-navy bg-white shadow-sticker">
            <div className="flex items-center justify-between border-b-2 border-monster-navy px-6 py-4">
                <h2 className="font-display text-lg font-semibold text-monster-navy">Launch List</h2>
                <span data-testid="subscriber-count" className="inline-flex items-center gap-2 rounded-full border-2 border-monster-navy bg-monster-yellow px-4 py-1.5 font-display text-sm font-semibold text-monster-navy shadow-sticker-sm">
                    <MailCheck className="h-4 w-4" /> {subs?.length ?? 0} signed up
                </span>
            </div>
            {subs === null ? (
                <p className="p-10 text-center font-semibold text-monster-navy/50">Loading subscribers...</p>
            ) : subs.length === 0 ? (
                <p data-testid="subscribers-empty" className="p-10 text-center font-semibold text-monster-navy/50">
                    No one on the launch list yet — share your site to start collecting emails.
                </p>
            ) : (
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow className="border-b-2 border-monster-navy">
                                <TableHead className="font-display font-semibold">Name</TableHead>
                                <TableHead className="font-display font-semibold">Email</TableHead>
                                <TableHead className="font-display font-semibold">Joined</TableHead>
                                <TableHead className="font-display font-semibold">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {subs.map((s) => (
                                <TableRow key={s.id} data-testid={`subscriber-row-${s.id}`} className="border-b border-monster-navy/10">
                                    <TableCell className="font-bold">{s.name}</TableCell>
                                    <TableCell>
                                        <a href={`mailto:${s.email}`} className="font-semibold text-monster-blue">{s.email}</a>
                                    </TableCell>
                                    <TableCell className="text-sm">{new Date(s.created_at).toLocaleDateString()}</TableCell>
                                    <TableCell>
                                        <button
                                            data-testid={`subscriber-delete-${s.id}`}
                                            onClick={() => remove(s.id)}
                                            className="rounded-lg border-2 border-monster-navy bg-white p-1.5 text-monster-red transition-colors hover:bg-red-50"
                                            aria-label="Remove subscriber"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}
            </div>
        </motion.div>
    );
};
